import { css, flex, Button, Text, Toast, tokens } from "@balabalabom/ui";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { miniBookService, getBaseUrl, type TemplateAsObject, type PaperSize } from "../lib/miniBookService";

const SPARKLE = "✨";
const ORANGE = "#e89b5f";
const ORANGE_DARK = "#d88a4f";
const CARD_BG = "#f8f6f2";
const BORDER = "#e8e4dc";

// Icons for instructions (emoji)
const ICON_NOTEBOOK = "📓";
const ICON_FOLD = "📖";
const ICON_PAGES = "📖";
const ICON_BOOK = "📕";

type CreateLocationState = { selectedInstructionBackground?: { id: number; title: string; url: string } };

export function MiniBookCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as CreateLocationState | null;
  const [name] = useState("My Mini Book");
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TemplateAsObject[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paperSize, setPaperSize] = useState<PaperSize>("LETTER");
  const [pageWidthInches, setPageWidthInches] = useState<number>(2.125);
  const [pageHeightInches, setPageHeightInches] = useState<number>(5.5);
  const [showInstructions, setShowInstructions] = useState(true);
  const [includeDetailInstructions, setIncludeDetailInstructions] = useState(true);
  const [showPageNumbers, setShowPageNumbers] = useState(true);
  const [foldLineColorHex, setFoldLineColorHex] = useState<string>("#0000FF");
  const [outlineLineColorHex, setOutlineLineColorHex] = useState<string>("#0000FF");
  const [cutLineColorHex, setCutLineColorHex] = useState<string>("#FF0000");
  const [instructionBackgrounds, setInstructionBackgrounds] = useState<{ id: number; title: string; url: string }[]>([]);
  const [bgIndex, setBgIndex] = useState(0);

  const template = templates[currentIndex] ?? null;
  const selectedInstructionBg = instructionBackgrounds[bgIndex] ?? null;
  const pageCount = template?.pageCount ?? 0;

  useEffect(() => {
    miniBookService
      .listInstructionBackgrounds(true, false)
      .then((list) => {
        const arr = list.map((b) => ({ id: b.id, title: b.title, url: b.url }));
        setInstructionBackgrounds(arr);
        const selected = state?.selectedInstructionBackground;
        if (selected) {
          const idx = arr.findIndex((b) => b.id === selected.id || b.url === selected.url);
          if (idx >= 0) setBgIndex(idx);
        }
      })
      .catch(() => setInstructionBackgrounds([]));
  }, [state?.selectedInstructionBackground]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setError(null);
        const list = await miniBookService.listTemplates();
        const published = list.filter((t) => t.status === "PUBLISHED");
        setTemplates(published);
        if (published.length > 0) {
          setCurrentIndex(0);
          const t = published[0];
          setSelectedPages(Array.from({ length: t?.pageCount ?? 0 }, (_, i) => i + 1));
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to load templates";
        setError(msg);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    const t = templates[currentIndex];
    if (t?.pageCount) {
      setSelectedPages(Array.from({ length: t.pageCount }, (_, i) => i + 1));
    }
  }, [currentIndex, templates]);

  const handlePrev = () => setCurrentIndex((i) => (i <= 0 ? templates.length - 1 : i - 1));
  const handleNext = () => setCurrentIndex((i) => (i >= templates.length - 1 ? 0 : i + 1));
  const handleBgPrev = () => setBgIndex((i) => (i <= 0 ? Math.max(0, instructionBackgrounds.length - 1) : i - 1));
  const handleBgNext = () => setBgIndex((i) => (i >= instructionBackgrounds.length - 1 ? 0 : i + 1));

  const exportDefaults = {
    paperSize,
    pageWidthInches,
    pageHeightInches,
    pageLabelPosition: "bottom-right" as const,
    showInstructions,
    instructionBackgroundPath: selectedInstructionBg?.url,
    includeDetailInstructions,
    foldLineColorHex,
    outlineLineColorHex,
    cutLineColorHex,
  };

  const handleCreateProject = async () => {
    if (!template?.id || selectedPages.length === 0) return;
    try {
      setCreating(true);
      setError(null);
      const projectId = await miniBookService.createProject(template.id, name, selectedPages);
      navigate(`/minibook/project/${projectId}`, { state: { exportDefaults } });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create project";
      setError(msg);
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleExportPdf = async () => {
    if (!template?.id || selectedPages.length === 0) return;
    try {
      setExporting(true);
      setError(null);
      setExportSuccess(null);
      const projectId = await miniBookService.createProject(template.id, name, selectedPages);
      const { pdfData, statusMessage } = await miniBookService.exportProject(projectId, exportDefaults);
      if (!pdfData || pdfData.length === 0) {
        setError(statusMessage || "Export failed: no PDF data received");
        return;
      }
      const buffer = pdfData.buffer.slice(pdfData.byteOffset, pdfData.byteOffset + pdfData.byteLength) as ArrayBuffer;
      const blob = new Blob([buffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setExportSuccess(statusMessage ? `PDF ready. ${statusMessage}` : "PDF ready! Opened in a new tab.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Export failed";
      setError(msg);
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <main className="main-mobile" style={css({ maxWidth: "80rem", margin: "0 auto", textAlign: "center", padding: "2rem" })}>
        <Text variant="body" style={css({ color: ORANGE })}>Loading templates...</Text>
      </main>
    );
  }

  if (error && templates.length === 0) {
    return (
      <main className="main-mobile" style={css({ maxWidth: "80rem", margin: "0 auto", textAlign: "center", padding: "2rem" })}>
        <Text variant="body" style={css({ color: "#ef4444", marginBottom: "1rem" })}>{error}</Text>
        <Button variant="primary" onClick={() => window.location.reload()} style={css({ backgroundColor: ORANGE })}>
          Retry
        </Button>
      </main>
    );
  }

  return (
    <main
      className="main-mobile"
      style={css({
        maxWidth: "80rem",
        margin: "0 auto",
        padding: "1.5rem",
        minHeight: "60vh",
        backgroundImage: `radial-gradient(circle at 20% 30%, rgba(253, 224, 71, 0.12) 0%, transparent 50%),
          radial-gradient(circle at 80% 70%, rgba(253, 224, 71, 0.08) 0%, transparent 40%)`,
      })}
    >
      {/* Single large card matching design */}
      <div
        style={css({
          backgroundColor: CARD_BG,
          borderRadius: "1.25rem",
          border: `1px solid ${BORDER}`,
          padding: "2rem",
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        })}
      >
        {/* Page title - centered */}
        <div style={css({ textAlign: "center", marginBottom: "2rem" })}>
          <h2
            style={css({
              fontSize: "1.75rem",
              fontWeight: 800,
              color: ORANGE,
              margin: 0,
              marginBottom: "0.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            })}
          >
            {SPARKLE} Start Your {pageCount}-Page Mini-Book Project! {SPARKLE}
          </h2>
          <Text variant="body" style={css({ color: tokens.colors.neutral[700] })}>
            Select a template and configure your mini-book.
          </Text>
        </div>

        <div className="minibook-create-grid">
          {/* Left: Select background paper + Configuration */}
          <div style={flex({ direction: "column", gap: "1.5rem" })}>
            {/* Select background paper */}
            <div>
              <h3 style={css({ fontSize: "1.25rem", fontWeight: 700, color: ORANGE, margin: 0, marginBottom: "1rem" })}>
                Select background paper
              </h3>
              {template && (
                <>
                  <div
                    style={css({
                      backgroundColor: "#fff",
                      borderRadius: "0.75rem",
                      border: `1px solid ${BORDER}`,
                      padding: "1.25rem",
                      marginBottom: "1rem",
                    })}
                  >
                    <Text variant="heading2" style={css({ marginBottom: "0.25rem" })}>
                      {template.title}
                    </Text>
                    <Text variant="body" style={css({ color: tokens.colors.neutral[600], fontSize: "0.9rem" })}>
                      {template.pageCount} pages • {template.sheetCount} sheet(s) • {template.paperSize}
                    </Text>
                    <Button
                      variant="primary"
                      size="sm"
                      style={css({
                        marginTop: "0.5rem",
                        backgroundColor: ORANGE,
                        borderRadius: "9999px",
                        "&:hover": { backgroundColor: ORANGE_DARK },
                      })}
                    >
                      Selected
                    </Button>

                    {/* Instruction background image preview */}
                    <div
                      style={css({
                        marginTop: "1.25rem",
                        padding: "1rem",
                        backgroundColor: "#faf9f7",
                        borderRadius: "0.5rem",
                        border: `1px dashed ${BORDER}`,
                        minHeight: 140,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      })}
                    >
                      {selectedInstructionBg ? (
                        <img
                          src={selectedInstructionBg.url.startsWith("http") ? selectedInstructionBg.url : `${getBaseUrl()}${selectedInstructionBg.url}`}
                          alt={selectedInstructionBg.title}
                          style={css({
                            maxWidth: "100%",
                            maxHeight: 200,
                            objectFit: "contain",
                          })}
                        />
                      ) : (
                        <Text variant="body" style={css({ color: tokens.colors.neutral[500], fontSize: "0.9rem" })}>
                          No instruction backgrounds available
                        </Text>
                      )}
                    </div>

                    {/* Carousel: Prev / dots / Next */}
                    {instructionBackgrounds.length > 1 && (
                      <div style={css({ ...flex({ justify: "center", align: "center", gap: "1rem" }), marginTop: "1rem" })}>
                        <button
                          type="button"
                          onClick={handleBgPrev}
                          style={css({
                            background: "none",
                            border: "none",
                            color: tokens.colors.neutral[600],
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            padding: "0.25rem 0.5rem",
                            "&:hover": { color: ORANGE },
                          })}
                        >
                          &lt; Prev
                        </button>
                        <div style={flex({ gap: "0.5rem" })}>
                          {instructionBackgrounds.map((_, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setBgIndex(i)}
                              style={css({
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                border: "none",
                                cursor: "pointer",
                                backgroundColor: i === bgIndex ? ORANGE : BORDER,
                                padding: 0,
                              })}
                            />
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={handleBgNext}
                          style={css({
                            background: "none",
                            border: "none",
                            color: tokens.colors.neutral[600],
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            padding: "0.25rem 0.5rem",
                            "&:hover": { color: ORANGE },
                          })}
                        >
                          Next &gt;
                        </button>
                      </div>
                    )}

                    {/* Template carousel - only if multiple templates */}
                    {templates.length > 1 && (
                      <div style={css({ ...flex({ justify: "center", align: "center", gap: "1rem" }), marginTop: "0.75rem" })}>
                        <button
                          type="button"
                          onClick={handlePrev}
                          style={css({
                            background: "none",
                            border: "none",
                            color: tokens.colors.neutral[600],
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            padding: "0.25rem 0.5rem",
                            "&:hover": { color: ORANGE },
                          })}
                        >
                          &lt; Template
                        </button>
                        <button
                          type="button"
                          onClick={handleNext}
                          style={css({
                            background: "none",
                            border: "none",
                            color: tokens.colors.neutral[600],
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            padding: "0.25rem 0.5rem",
                            "&:hover": { color: ORANGE },
                          })}
                        >
                          Template &gt;
                        </button>
                      </div>
                    )}

                    <Button
                      variant="primary"
                      style={css({
                        width: "100%",
                        marginTop: "1rem",
                        backgroundColor: ORANGE,
                        borderRadius: "9999px",
                        "&:hover": { backgroundColor: ORANGE_DARK },
                      })}
                      onClick={() => navigate("/minibook/instructionBackground")}
                    >
                      Select background paper &gt;
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Configuration */}
            <div>
              <h3 style={css({ fontSize: "1.25rem", fontWeight: 700, color: ORANGE, margin: 0, marginBottom: "1rem" })}>
                Configuration
              </h3>
              <div style={flex({ direction: "column", gap: "1.25rem" })}>
                <div>
                  <label style={css({ fontWeight: 600, fontSize: "0.9rem", color: tokens.colors.neutral[700], display: "block", marginBottom: "0.5rem" })}>
                    Page Size
                  </label>
                  <div style={flex({ gap: "1rem", wrap: "wrap" })}>
                    {(["LETTER", "A4", "A5", "LEGAL"] as const).map((size) => (
                      <label
                        key={size}
                        style={css({
                          ...flex({ align: "center", gap: "0.5rem" }),
                          cursor: "pointer",
                          padding: "0.5rem 0.75rem",
                          borderRadius: "0.5rem",
                          border: `2px solid ${paperSize === size ? ORANGE : BORDER}`,
                          backgroundColor: paperSize === size ? "rgba(232,155,95,0.1)" : "transparent",
                        })}
                      >
                        <span style={css({ fontSize: "1.25rem" })}>📄</span>
                        <input
                          type="radio"
                          name="paperSize"
                          value={size}
                          checked={paperSize === size}
                          onChange={() => setPaperSize(size)}
                          style={css({ accentColor: ORANGE })}
                        />
                        <Text variant="body">{size === "LETTER" ? "Letter" : size === "LEGAL" ? "Legal" : size}</Text>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={flex({ gap: "1rem", wrap: "wrap" })}>
                  <div>
                    <label style={css({ fontWeight: 600, fontSize: "0.9rem", color: tokens.colors.neutral[700], display: "block", marginBottom: "0.5rem" })}>
                      Page Width (in)
                    </label>
                    <input
                      type="number"
                      min={0.5}
                      max={24}
                      step={0.125}
                      value={pageWidthInches}
                      onChange={(e) => setPageWidthInches(parseFloat(e.target.value) || 2.125)}
                      style={css({
                        width: "6rem",
                        padding: "0.5rem 0.75rem",
                        borderRadius: "0.5rem",
                        border: `1px solid ${BORDER}`,
                        fontSize: "1rem",
                      })}
                    />
                  </div>
                  <div>
                    <label style={css({ fontWeight: 600, fontSize: "0.9rem", color: tokens.colors.neutral[700], display: "block", marginBottom: "0.5rem" })}>
                      Page Height (in)
                    </label>
                    <input
                      type="number"
                      min={0.5}
                      max={24}
                      step={0.125}
                      value={pageHeightInches}
                      onChange={(e) => setPageHeightInches(parseFloat(e.target.value) || 5.5)}
                      style={css({
                        width: "6rem",
                        padding: "0.5rem 0.75rem",
                        borderRadius: "0.5rem",
                        border: `1px solid ${BORDER}`,
                        fontSize: "1rem",
                      })}
                    />
                  </div>
                </div>

                <div style={flex({ align: "center", justify: "between" })}>
                  <label style={css({ fontWeight: 600, fontSize: "0.9rem", color: tokens.colors.neutral[700] })}>
                    Page Numbers?
                  </label>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showPageNumbers}
                    onClick={() => setShowPageNumbers((v) => !v)}
                    style={css({
                      width: 48,
                      height: 26,
                      borderRadius: 13,
                      border: "none",
                      cursor: "pointer",
                      backgroundColor: showPageNumbers ? "#22c55e" : BORDER,
                      position: "relative",
                      transition: "background-color 0.2s",
                    })}
                  >
                    <span
                      style={css({
                        position: "absolute",
                        top: 2,
                        left: showPageNumbers ? 24 : 2,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: "#fff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        transition: "left 0.2s",
                      })}
                    />
                  </button>
                </div>

                <div>
                  <label style={css({ fontWeight: 700, fontSize: "0.9rem", color: tokens.colors.neutral[800], display: "block", marginBottom: "0.5rem" })}>
                    Guide line colors
                  </label>
                  <div style={css({ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "0.75rem" })}>
                    {[
                      {
                        label: "Fold",
                        value: foldLineColorHex,
                        onChange: setFoldLineColorHex,
                      },
                      {
                        label: "Outline",
                        value: outlineLineColorHex,
                        onChange: setOutlineLineColorHex,
                      },
                      {
                        label: "Cut",
                        value: cutLineColorHex,
                        onChange: setCutLineColorHex,
                      },
                    ].map(({ label, value, onChange }) => (
                      <div key={label} style={css({ border: `1px solid ${BORDER}`, borderRadius: "0.75rem", padding: "0.75rem", backgroundColor: "#fff" })}>
                        <Text variant="body" style={css({ fontWeight: 600, marginBottom: "0.5rem" })}>
                          {label}
                        </Text>
                        <div style={flex({ align: "center", gap: "0.5rem" })}>
                          <input
                            type="color"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            aria-label={`${label} line color`}
                            style={css({ width: 40, height: 32, padding: 0, border: "none", background: "transparent", cursor: "pointer" })}
                          />
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="#RRGGBB"
                            style={css({
                              flex: 1,
                              padding: "0.5rem 0.6rem",
                              borderRadius: "0.5rem",
                              border: `1px solid ${BORDER}`,
                              fontSize: "0.9rem",
                              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                            })}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={css({ marginTop: "0.5rem" })}>
                    <button
                      type="button"
                      onClick={() => {
                        setFoldLineColorHex("#0000FF");
                        setOutlineLineColorHex("#0000FF");
                        setCutLineColorHex("#FF0000");
                      }}
                      style={css({
                        background: "none",
                        border: "none",
                        color: tokens.colors.neutral[600],
                        cursor: "pointer",
                        padding: 0,
                        fontSize: "0.85rem",
                        textDecoration: "underline",
                        "&:hover": { color: ORANGE },
                      })}
                    >
                      Reset to defaults
                    </button>
                  </div>
                </div>
              </div>

              {error && (
                <Text variant="body" style={css({ color: "#ef4444", marginTop: "0.75rem" })}>
                  {error}
                </Text>
              )}

              <Button
                variant="primary"
                style={css({
                  width: "100%",
                  marginTop: "1.25rem",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  backgroundColor: ORANGE,
                  borderRadius: "9999px",
                  "&:hover:not(:disabled)": { backgroundColor: ORANGE_DARK },
                })}
                onClick={handleCreateProject}
                disabled={selectedPages.length === 0 || creating}
              >
                {creating ? "Creating…" : "Create Project & Customize"}
              </Button>
              <Button
                variant="outline"
                style={css({
                  width: "100%",
                  marginTop: "0.5rem",
                  padding: "0.75rem",
                  fontSize: "0.95rem",
                  borderColor: BORDER,
                  color: tokens.colors.neutral[700],
                })}
                onClick={handleExportPdf}
                disabled={selectedPages.length === 0 || exporting}
              >
                {exporting ? "Exporting…" : "Quick Export PDF (no customization)"}
              </Button>
            </div>
          </div>

          {/* Right: Instructions sidebar */}
          <div
            className="minibook-create-instructions"
            style={css({
              backgroundColor: "#fff",
              borderRadius: "0.75rem",
              border: `1px solid ${BORDER}`,
              padding: "1.5rem",
            })}
          >
            <div style={flex({ direction: "column", gap: "1.25rem" })}>
              {[
                { num: 1, label: "DRAW", icon: ICON_NOTEBOOK },
                { num: 2, label: "FOLD", icon: ICON_FOLD },
                { num: 3, label: "Page Numbers", icon: ICON_PAGES },
                { num: 4, label: "Show Instructions?", icon: ICON_BOOK, toggle: "showInstructions" },
                { num: 5, label: "Include Detail Instructions", icon: ICON_BOOK, toggle: "includeDetailInstructions" },
              ].map(({ num, label, icon, toggle }) => (
                <div key={num} style={flex({ align: "center", gap: "0.75rem" })}>
                  <span style={css({ fontSize: "1.5rem", flexShrink: 0 })}>{icon}</span>
                  <div style={css({ ...flex({ direction: "column", gap: "0.25rem" }), flex: 1 })}>
                    <Text variant="body" style={css({ fontWeight: 600 })}>
                      {num} {label}
                    </Text>
                    {toggle === "showInstructions" && (
                      <div style={flex({ align: "center", gap: "0.5rem" })}>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={showInstructions}
                          onClick={() => setShowInstructions((v) => !v)}
                          style={css({
                            width: 44,
                            height: 24,
                            borderRadius: 12,
                            border: "none",
                            cursor: "pointer",
                            backgroundColor: showInstructions ? "#22c55e" : BORDER,
                            position: "relative",
                            transition: "background-color 0.2s",
                          })}
                        >
                          <span
                            style={css({
                              position: "absolute",
                              top: 2,
                              left: showInstructions ? 22 : 2,
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              backgroundColor: "#fff",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                              transition: "left 0.2s",
                            })}
                          />
                        </button>
                        <Text variant="body" style={css({ fontSize: "0.85rem", color: tokens.colors.neutral[600] })}>
                          {showInstructions ? "On" : "Off"}
                        </Text>
                      </div>
                    )}
                    {toggle === "includeDetailInstructions" && (
                      <div style={flex({ align: "center", gap: "0.5rem" })}>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={includeDetailInstructions}
                          onClick={() => setIncludeDetailInstructions((v) => !v)}
                          style={css({
                            width: 44,
                            height: 24,
                            borderRadius: 12,
                            border: "none",
                            cursor: "pointer",
                            backgroundColor: includeDetailInstructions ? "#22c55e" : BORDER,
                            position: "relative",
                            transition: "background-color 0.2s",
                          })}
                        >
                          <span
                            style={css({
                              position: "absolute",
                              top: 2,
                              left: includeDetailInstructions ? 22 : 2,
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              backgroundColor: "#fff",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                              transition: "left 0.2s",
                            })}
                          />
                        </button>
                        <Text variant="body" style={css({ fontSize: "0.85rem", color: tokens.colors.neutral[600] })}>
                          {includeDetailInstructions ? "On" : "Off"}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <Toast variant="error" message={error} onClose={() => setError(null)} />
      )}
      {exportSuccess && !error && (
        <Toast
          variant="success"
          message={exportSuccess}
          onClose={() => setExportSuccess(null)}
        />
      )}
    </main>
  );
}
